import { gql } from '@apollo/client'

const USER_BY_ADMIN_FIELDS = gql`
  fragment UserByAdminFields on UsersByAdminType {
    id
    avatar
    firstName
    lastName
    email
    role
    purchases {
      active
      activeCount
      paused
      pausedCount
      expired
      expiredCount
    }
    expiration
    membershipLvl
  }
`

const PLATFORM_FIELDS_SHORT = gql`
  fragment PlatformFieldsShort on PlatformType {
    id
    slug
    title
  }
`

const USER_BASE_FIELDS = gql`
  fragment UserBaseFields on UserType {
    id
    email
    firstName
    lastName
    selectedPlatform
    selectedPlatformId
    role
    isBetaTester
    isBlocked
    blockReason
    membershipUpdatedAt
    facultyModeSchool
    facultyModeCohort
    facultyModeProduct
    ugcCreationDataStatus
    getAvatar {
      photo
    }
    isMFAMandatory
    screenshotInfo {
      platform
      totalScreenshotsCount
    }
  }
`

const USER_FIELDS = gql`
  ${USER_BASE_FIELDS}
  ${PLATFORM_FIELDS_SHORT}
  fragment UserFields on UserType {
    ...UserBaseFields
    membership {
      id
      platform {
        ...PlatformFieldsShort
      }
      type
      expiresTo
      startDate
      pausedAt
      pauseTokensCount
      pauseTokensUsed
      additionalTestsSubjectIds
      status
      isExtended
      order
      duration
      extensionType
      subscriptionId
      subscriptionStatus
    }
  }
`

export const GET_USERS = gql`
  ${USER_BY_ADMIN_FIELDS}
  query findAllUsers($query: UserQueryInput!) {
    findAllUsers(query: $query) {
      count
      items {
        ...UserByAdminFields
      }
    }
  }
`

export const GET_USERS_COUNT_BY_ROLES = gql`
  query findUsersCountByRoles {
    findUsersCountByRoles {
      all
      superAdmins
      admins
      students
      aiTrainers
      teacher
      marketing
      blocked
    }
  }
`

export const GET_USER = gql`
  ${USER_FIELDS}
  query findOneUser($id: String!) {
    findOneUser(id: $id) {
      ...UserFields
    }
  }
`

export const UPDATE_USER = gql`
  ${USER_FIELDS}
  mutation updateUser($updateUserInput: UpdateUserInput!) {
    updateUser(updateUserInput: $updateUserInput) {
      ...UserFields
    }
  }
`

export const UPDATE_USER_BY_ADMIN = gql`
  ${USER_BASE_FIELDS}
  mutation updateUserByAdmin($updateUserInput: UserInput!, $membershipUpdatedOn: PlatformName) {
    updateUserByAdmin(updateUserInput: $updateUserInput, membershipUpdatedOn: $membershipUpdatedOn) {
      ...UserBaseFields
    }
  }
`

export const UPDATE_USER_MEMBERSHIP_BY_ADMIN = gql`
  mutation updateUserMembershipByAdmin(
    $updateUserMembershipByAdminInput: MembershipInput!
    $membershipUpdatedAt: DateTime
  ) {
    updateUserMembershipByAdmin(
      updateUserMembershipByAdminInput: $updateUserMembershipByAdminInput
      membershipUpdatedAt: $membershipUpdatedAt
    ) {
      id
    }
  }
`

export const GET_IP_LOG = gql`
  query findAllIpLog($query: IpLogQueryInput!) {
    findAllIpLog(query: $query) {
      count
      items {
        id
        deviceType
        createdAt
        ip
        browserId
        userAgent
        city
        region
      }
    }
  }
`

export const RESET_PASSWORD = gql`
  mutation resetPassword($email: String!) {
    resetPassword(email: $email)
  }
`

export const SET_PASSWORD_BY_ADMIN = gql`
  mutation setPasswordByAdmin($userId: String!, $newPassword: String!) {
    setPasswordByAdmin(userId: $userId, newPassword: $newPassword)
  }
`

export const FORCE_USER_LOGOUT = gql`
  mutation forceUserLogout($userId: String!) {
    forceUserLogout(userId: $userId)
  }
`

export const BLOCK_USER_BY_ADMIN = gql`
  mutation blockUserByAdmin($userId: String!, $reason: String!) {
    blockUserByAdmin(userId: $userId, reason: $reason)
  }
`

export const UNBLOCK_USER_BY_ADMIN = gql`
  mutation unblockUserByAdmin($userId: String!) {
    unblockUserByAdmin(userId: $userId)
  }
`

export const GET_MEMBERSHIP_LOG = gql`
  query findAllMembershipLog($query: MembershipLogQueryInput!) {
    findAllMembershipLog(query: $query) {
      count
      items {
        id
        createdAt
        version
        platform
        log
      }
    }
  }
`

export const GET_REWARD_ITEMS_LOG = gql`
  query findAllRewardItemsLog($query: UserRewardLogQueryInput!) {
    findAllRewardItemsLog(query: $query) {
      count
      items {
        id
        createdAt
        platform
        log
      }
    }
  }
`

export const GET_USERS_PROGRESS = gql`
  query findUsersProgress($platformId: String!, $findUsersProgressId: String!) {
    findUsersProgress(platformId: $platformId, id: $findUsersProgressId) {
      subject {
        id
        icon
        tabName
        slug
        order
        questionBankDescription
      }
      questionProgress {
        correctAnswersCount
        incorrectAnswersCount
        finishedQuestionsCount
        totalQuestionsCount
      }
      videoProgress {
        watchedVideosCount
        nextVideoId
        nextVideoCategoryId
        totalVideosCount
      }
      practiceTestsProgress {
        completedPracticeTests
        totalPracticeTests
      }
    }
  }
`

export const GET_USERS_CASPER_PROGRESS = gql`
  query findUsersCasperProgress($id: String!, $isFirstAttempt: Boolean) {
    findUsersCasperProgress(id: $id, isFirstAttempt: $isFirstAttempt) {
      videoResponsePractice {
        answeredQuestionsCount
        totalQuestionsCount
      }
      textResponsePractice {
        answeredQuestionsCount
        totalQuestionsCount
      }
      scenarioQBank {
        answeredQuestionsCount
        totalQuestionsCount
      }
    }
  }
`

export const GET_USERS_PRACTICE_TEST_PROGRESS = gql`
  query findUsersPracticeTestsProgress($userId: String!, $skip: Int, $platformId: String!, $take: Int) {
    findUsersPracticeTestsProgress(userId: $userId, skip: $skip, platformId: $platformId, take: $take) {
      items {
        practiceTestId
        userId
        createdAt
        attemptNumber
        totalTimeSpentCount
        maxDatScore
        estimatedDatScore
        title
        subjectId
        subjectTitle
        attemptId
      }
      count
    }
  }
`

export const FIND_ALL_USER_PRACTICE_TEST_STATS = gql`
  query findAllUserPracticeTestStats($query: UserStatsPracticeTestQueryInput!) {
    findAllUserPracticeTestStats(query: $query) {
      count
      items {
        id
        createdAt
        attemptNumber
        answeredCorrectlyCount
        totalQuestionsCount
        totalTimeSpentCount
        markedQuestionCount
        avgStudentScore
        estimatedDATScore
        feedback
        reportByQuestionCategory {
          totalAnswersCount
          timeSpentInPercentage
          timeSpent
          questionCategoryName
          correctAnswersCount
        }
        practiceTest {
          id
          title
          order
          duration
          subject {
            id
            slug
            tabName
          }
        }
      }
    }
  }
`

export const GENERATE_CSV = gql`
  query GenerateCSV($query: UserQueryInput!) {
    generateCSV(query: $query)
  }
`

export const FIND_ALL_USER_BLOCK_HISTORY = gql`
  query findAllUserBlockHistory($query: UserBlockHistoryQueryInput!) {
    findAllUserBlockHistory(query: $query) {
      count
      items {
        id
        createdAt
        counter
        status
      }
    }
  }
`

export const FIND_ALL_USER_REWARDS_BY_ADMIN = gql`
  query findAllUserRewardByAdmin($user: LinkUserInput!, $query: UserRewardQueryInput!) {
    findAllUserRewardByAdmin(user: $user, query: $query) {
      items {
        id
        availableCount
        usedCount
        reward {
          id
          type
          value
        }
      }
    }
  }
`

export const UPDATE_USER_REWARD_BY_ADMIN = gql`
  mutation updateUserRewardByAdmin($input: UpdateUserRewardInput!) {
    updateUserRewardByAdmin(updateUserRewardByAdminInput: $input) {
      id
      availableCount
    }
  }
`

export const FIND_USER_MFA_INFO = gql`
  query findUserMfaInfo($userId: String!) {
    findUserMfaInfo(userId: $userId) {
      isMfaEnabled
      mfaType
      phoneNumber
    }
  }
`

export const ENABLE_MFA_BY_ADMIN = gql`
  mutation enableMfaByAdmin($userId: String!) {
    enableMfaByAdmin(userId: $userId)
  }
`

export const DISABLE_MFA_BY_ADMIN = gql`
  mutation disableMfaByAdmin($userId: String!) {
    disableMfaByAdmin(userId: $userId)
  }
`

export const GENERATE_UGC_DATA = gql`
  mutation generateUGCData($userId: String!, $platformId: String!) {
    generateUGCData(userId: $userId, platformId: $platformId)
  }
`
